import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Plane, Megaphone, BellRing, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClockPanel } from "@/components/app/ClockPanel";
import { InstallApp } from "@/components/app/InstallApp";
import { enablePush } from "@/components/app/NotificationBell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const aud = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tile = ({ label, value, to, icon: Icon, testid }) => {
  const body = (
    <div className="h-full rounded-2xl border border-[#E9EEF2] bg-white p-4" data-testid={testid}>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</p>
      <p className="mt-1 break-words font-display text-2xl font-bold text-[#0B2D5B]">{value}</p>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};

const NotifyPrompt = () => {
  const [show, setShow] = useState(typeof Notification !== "undefined" && Notification.permission === "default" && !localStorage.getItem("clockaroo_notify_dismissed"));
  if (!show) return null;
  return (
    <div className="rounded-2xl border border-[#0B2D5B]/10 bg-[#0B2D5B] p-5 text-white" data-testid="notify-prompt">
      <BellRing className="h-6 w-6 text-[#FF8A00]" />
      <p className="mt-2 font-semibold">Never miss a shift</p>
      <p className="mt-1 text-sm text-slate-200">Get a reminder before your shifts, if you forget to clock in or out, and when your leave is approved or a new announcement is posted.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button className="h-11 rounded-full bg-[#00C16B] px-5 font-semibold hover:bg-[#00A85C]" onClick={async () => { await enablePush(); setShow(false); }} data-testid="notify-enable-btn">Enable notifications</Button>
        <Button variant="ghost" className="h-11 rounded-full text-white hover:bg-white/10" onClick={() => { localStorage.setItem("clockaroo_notify_dismissed", "1"); setShow(false); }} data-testid="notify-later-btn">Not now</Button>
      </div>
    </div>
  );
};

const RosterToday = ({ shifts }) => (
  <div className="rounded-2xl border border-[#E9EEF2] bg-white p-4" data-testid="home-roster">
    <p className="flex items-center gap-1.5 font-semibold text-[#0B2D5B]"><CalendarDays className="h-4 w-4" />Today's roster</p>
    {shifts.length ? shifts.map((s) => <p key={s.shift_id || s.start} className="mt-2 break-words text-sm"><b>{s.start}–{s.end}</b>{s.workplace_name && <span className="text-slate-500"> · {s.workplace_name}</span>}{s.role && <span className="text-slate-500"> · {s.role}</span>}</p>)
      : <p className="mt-2 text-sm text-slate-500">No rostered shift today.</p>}
    <Link to="/app/rosters" className="mt-3 inline-block text-sm font-semibold text-[#00A85C]" data-testid="home-roster-link">See my roster</Link>
  </div>
);

export const EmployeeHome = () => {
  const { membership } = useAuth();
  const [h, setH] = useState(null);
  const [pending, setPending] = useState(0);
  const [unread, setUnread] = useState(0);
  const full = membership.app_access !== "leave_only";
  useEffect(() => {
    api.get("/me/home").then((r) => setH(r.data)).catch(() => {});
    api.get("/leave").then((r) => setPending(r.data.filter((l) => l.status === "pending" && l.membership_id === membership.membership_id).length)).catch(() => {});
    api.get("/announcements").then((r) => setUnread(r.data.filter((a) => !a.my_read).length)).catch(() => {});
  }, [membership.membership_id]);
  return (
    <div className="mx-auto w-full max-w-xl space-y-4" data-testid="employee-dashboard">
      <div>
        {!full && <p className="text-sm text-slate-500">{membership.business_name}</p>}
        <h1 className="font-display text-2xl font-bold text-[#0B2D5B] sm:text-3xl" data-testid="home-welcome">G'day, {h?.first_name || "there"}</h1>
        {!full && h?.workplaces?.length > 0 && <p className="mt-1 flex flex-wrap items-center gap-1 text-sm text-slate-600" data-testid="employee-workplaces"><MapPin className="h-4 w-4 text-[#00C16B]" />{h.workplaces.join(" · ")}</p>}
      </div>
      {full ? <ClockPanel /> : (
        <div className="rounded-2xl bg-[#0B2D5B] p-6 text-white"><Clock className="h-6 w-6 text-[#FF8A00]" /><p className="mt-3 font-semibold">Leave & announcements</p><p className="mt-1 text-sm text-slate-200">Request days off and keep up with team news.</p>
          <Link to="/app/leave"><Button className="mt-4 h-11 rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid="employee-go-leave-btn">Request leave</Button></Link></div>
      )}
      {full && h && (
        <div className="grid grid-cols-2 gap-3">
          <Tile label="Today" value={`${h.hours_today} h`} to="/app/timesheets" testid="home-hours-today" />
          <Tile label="This week" value={`${h.hours_week} h`} to="/app/timesheets" testid="home-hours-week" />
          {h.est_gross_week != null && <Tile label="Est. gross this week" value={aud(h.est_gross_week)} testid="home-est-gross" />}
          <Tile label="Pending leave" value={pending} to="/app/leave" icon={Plane} testid="home-pending-leave" />
          <Tile label="Unread news" value={unread} to="/app/announcements" icon={Megaphone} testid="home-unread" />
        </div>
      )}
      {full && h && <RosterToday shifts={h.roster_today} />}
      {!full && <Link to="/app/rosters" className="flex items-center justify-between rounded-2xl border border-[#00C16B]/40 bg-white p-4" data-testid="home-my-roster"><span className="flex items-center gap-2 font-semibold text-[#0B2D5B]"><CalendarDays className="h-5 w-5 text-[#00A85C]" />My roster</span><span className="text-sm font-semibold text-[#00A85C]">View & confirm</span></Link>}
      {!full && <div className="grid grid-cols-2 gap-3"><Tile label="Pending leave" value={pending} to="/app/leave" icon={Plane} testid="home-pending-leave" /><Tile label="Unread news" value={unread} to="/app/announcements" icon={Megaphone} testid="home-unread" /></div>}
      {full && h?.est_gross_week != null && <p className="text-xs text-slate-400">Estimated gross pay is a guide only, based on hours recorded this week. Your final pay is set when payroll is approved.</p>}
      <NotifyPrompt />
      <InstallApp />
    </div>
  );
};
